/**
 * Accessible pagination — shared by shop, tunes, and articles (/tunes layout).
 */
import React, { useId } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './PaginationBar.css';

function buildPageNumbers(currentPage, totalPages) {
  const items = [];
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    const nearCurrent =
      pageNumber === 1 ||
      pageNumber === totalPages ||
      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);
    const ellipsisGap =
      pageNumber === currentPage - 2 || pageNumber === currentPage + 2;

    if (nearCurrent) {
      items.push({ type: 'page', pageNumber });
    } else if (ellipsisGap) {
      items.push({ type: 'ellipsis', key: `ellipsis-${pageNumber}` });
    }
  }
  return items;
}

const PaginationBar = ({
  currentPage,
  totalPages,
  totalItems,
  indexOfFirst,
  indexOfLast,
  itemLabel = 'items',
  pageSize,
  pageSizeOptions = [25, 50, 75],
  onPageChange,
  onPageSizeChange,
  ariaLabel = 'Pagination',
  className = '',
  showNav = totalPages > 1,
  showPageSize = totalItems > 0,
  showStatus = totalItems > 0,
}) => {
  const { t } = useLanguage();
  const pg = t.shop.pagination;
  const pageSizeLabelId = useId();
  const pageItems = buildPageNumbers(currentPage, totalPages);
  const showNavControls = showNav && totalPages > 1;
  const isSinglePage = totalPages <= 1;

  if (!showNavControls && !showPageSize) return null;

  const navControls = showNavControls ? (
    <>
      <button
        type="button"
        className="pagination-btn"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label={pg.previous}
      >
        &#8249;
      </button>

      <div className="pagination-numbers">
        {pageItems.map((item) => {
          if (item.type === 'ellipsis') {
            return (
              <span key={item.key} className="pagination-ellipsis" aria-hidden="true">
                &hellip;
              </span>
            );
          }
          const { pageNumber } = item;
          const isCurrent = currentPage === pageNumber;
          return (
            <button
              key={pageNumber}
              type="button"
              className={`pagination-number${isCurrent ? ' active' : ''}`}
              onClick={() => onPageChange(pageNumber)}
              aria-label={isCurrent ? fmt(pg.pageCurrent, { n: pageNumber }) : fmt(pg.page, { n: pageNumber })}
              aria-current={isCurrent ? 'page' : undefined}
            >
              {pageNumber}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="pagination-btn"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label={pg.next}
      >
        &#8250;
      </button>
    </>
  ) : null;

  const statusText =
    showStatus && totalPages > 1 && indexOfFirst != null && indexOfLast != null
      ? fmt(pg.ofTotal, { range: `${indexOfFirst}\u2013${Math.min(indexOfLast, totalItems)}`, total: totalItems })
      : showStatus && totalItems > 0
        ? `${totalItems} ${totalItems === 1 ? itemLabel.replace(/s$/, '') : itemLabel}`
        : null;

  const pageSizeControl = showPageSize ? (
    <>
      <span className="pagination-pagesize-label" id={pageSizeLabelId}>
        {pg.perPage}
      </span>
      <div
        className="pagination-pagesize-group"
        role="group"
        aria-labelledby={pageSizeLabelId}
      >
        {pageSizeOptions.map((n) => (
          <button
            key={n}
            type="button"
            className={`pagination-pagesize-btn${pageSize === n ? ' active' : ''}`}
            onClick={() => onPageSizeChange(n)}
            aria-pressed={pageSize === n}
            aria-label={fmt(pg.showPerPage, { n, label: itemLabel })}
          >
            {n}
          </button>
        ))}
      </div>
    </>
  ) : null;

  const footer = (statusText || pageSizeControl) && (
    <div className="pagination-per-page">
      {statusText && (
        <p className="pagination-info" aria-live="polite" aria-atomic="true">
          {statusText}
        </p>
      )}
      {pageSizeControl}
    </div>
  );

  return (
    <nav
      className={[
        'pagination',
        isSinglePage && showPageSize ? 'pagination--single' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={ariaLabel}
    >
      {navControls}
      {footer}
    </nav>
  );
};

export default PaginationBar;
